<template>
  <section class="custom-matrix-wrapper">
    <z-tabs v-model="currentTab" :tabs="tabs" @change="showTab" />
    <!-- View Matrix by System -->
    <div v-if="currentTab === 'by System'" class="row">
      <div class="col">
        <p>
          ADAS MAP utilizes a VIN Decoder service to identify Vehicle ADAS and Safety systems. The results from this decoding process will categorize
          similar items in separate sub-categories.
        </p>
        <p>
          You can use this facility to pull these sub-categories into a single, more consise Matrix Category which is presented to users using ADAS
          MAP. This can better organize their inspection tasks. To do so, use the Add button (below) to add category matrices. For each Matrix
          Category, add those decoded sub-categories (<b>exact match</b>) that'll fall under this single category matrix.
        </p>
        <p>
          NOTE: you'll also need to create any Matrix Categories for those categories you wish to highlight and associate with a notice. These will
          also appear on reports.
        </p>
        <!-- Listing Actions -->
        <div class="row">
          <div class="col-md-2">
            <button class="btn btn-primary btn-small mb-2" @click="addAdasMatrix">Add</button>
          </div>
          <div class="col-md-3">
            <z-select v-model="filter" label="Make" :options="vehicles" :inline="true" @change="refreshList" />
          </div>
          <div class="col-md-2">
            <div class="custom-show-checkbox">
              <input v-model="showInactive" type="checkbox" class="custom-checkbox" @change="refreshList" />
              <span class="ms-2">Show Inactive?</span>
            </div>
          </div>
          <div class="col">
            <div class="custom-right-align">
              <button class="btn btn-primary btn-sm" :disabled="isDownloading" @click="exportMatrixVehicleTriggers">
                <FontAwesomeIcon v-if="isDownloading" icon="fa-solid fa-spinner me-2" spin />
                Export Matrix Vehicle Triggers
              </button>
            </div>
          </div>
        </div>
        <!-- Listing -->
        <div v-show="loading?.adasMatrix" class="listingWrapper">
          <div class="custom-spinner"><i class="fas fa-spinner fa-spin"></i></div>
        </div>
        <div v-show="!loading.adasMatrix" ref="listingWrapper" class="listingWrapper">
          <z-table
            v-model="adasmatrices"
            class="table table-bordered table-striped"
            :is-single-row-detail="true"
            sort-on="sort"
            sort-dir="asc"
            :columns="tableColumnsName"
            :detail-column-class="detailColumnClass"
            :is-header-fixed="true"
            @sort="setOrder"
          >
            <template #tbody="props">
              <td class="custom-column-60">
                <a href="#" @click="editAdasMatrix(props.row)">edit</a>
              </td>
              <td>{{ props.row.matrixItem }}</td>
              <td class="custom-column-110 text-center">{{ props.row.numCategories }}</td>
              <td class="custom-column-100 text-center">{{ props.row.numMakes }}</td>
              <td class="custom-column-80 text-center">{{ props.row.numInfoItems }}</td>
              <td class="custom-column-60 text-center">{{ props.row.isInactive ? 'Y' : '' }}</td>
              <td class="custom-column-60">
                <a href="#" @click="confirmDelete(props.row)">delete</a>
              </td>
            </template>
            <template #detail="props">
              <adas-matrix-record :matrix="props.row" />
            </template>
            <template #empty> Nothing found! </template>
          </z-table>
        </div>
      </div>
    </div>
    <!-- View Matrix by Vehicle -->
    <div v-if="currentTab === 'by Vehicle'" class="row mt-2">
      <div class="col">
        <div class="row">
          <div class="col-6">
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text">Make</span>
              </div>
              <select v-model="vehicleMake" class="form-control" @change="changedMake">
                <option v-for="item of vehicles" :key="item.id" :value="item.value">{{ item.name }}</option>
              </select>
              <div class="input-group-append">
                <span id="basic-addon1" class="input-group-text">Model</span>
              </div>
              <input v-model="vehicleModel" type="text" class="form-control" placeholder="Model" aria-label="Model" @keyup="changedMake" />
              <div class="input-group-append">
                <button class="btn btn-primary" :disabled="!isReady" @click="fetchMatrices">fetch</button>
              </div>
            </div>
          </div>
          <div class="col"></div>
        </div>
        <div class="row mt-2">
          <div class="col">
            <adas-matrix-vehicle ref="adasMatrixByVehicle" :vehicle-make="vehicleMake" @refresh="fetchMatrices" />
          </div>
        </div>
      </div>
    </div>
    <!-- Dialogs -->
    <dialog-matrix v-model="actionMatrix" @closing="closeModal" @saved="savedAdasMatrix" />
    <confirm-delete
      v-model="actionDelete"
      :confirm-title="confirmTitle"
      :confirm-action="confirmAction"
      :confirm-question="confirmQuestion"
      @closing="closeModal"
      @proceed="performDelete"
    />
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { adasmatrixStore } from '@/stores/adasmatrix'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ZTable from '@/components/utilities/elements/Table.vue'
import ZSelect from '@/components/utilities/elements/Select.vue'
import ZTabs from '@/components/utilities/elements/Tabs.vue'
import ConfirmDelete from '@/components/utilities/elements/ConfirmDialog.vue'
import AdasMatrixRecord from './adas-matrix-record.vue'
import DialogMatrix from './dialog-matrix.vue'
import AdasMatrixVehicle from './adas-matrix-vehicle.vue'
export default {
  name: 'AdasMatrix',
  components: {
    ZTable,
    ZSelect,
    ZTabs,
    ConfirmDelete,
    AdasMatrixRecord,
    DialogMatrix,
    AdasMatrixVehicle,
    FontAwesomeIcon
  },
  data() {
    return {
      showInactive: false,
      currentTab: 'by System',
      tabs: ['by System', 'by Vehicle'],
      orderBy: 'matrixItem',
      sortBy: 'asc',
      actionMatrix: null,
      detailColumnClass: 'custom-column-60',
      actionDelete: null,
      confirmTitle: 'Delete this ADAS Matrix?',
      confirmAction: 'Delete',
      confirmQuestion: 'Are you sure you wish to Delete this ADAS Matrix?',
      filter: '-9999',
      vehicleMake: null,
      vehicleModel: null,
      isReady: false,
      isDownloading: false
    }
  },
  async mounted() {
    this.showInactive = false
    this.isDownloading = false
    this.filter = '-9999'
    await this.refreshList()
  },
  computed: {
    ...mapState(adasmatrixStore, ['loading', 'lookups', 'adasmatrices', 'adasmatrix', 'matrixKeywordsTriggers']),
    tableColumnsName() {
      return [
        {
          field: 'adasChecklistMatrix',
          label: '',
          sortable: false,
          class: 'custom-column-60'
        },
        {
          field: 'matrixItem',
          label: 'Item',
          sortable: true
        },
        {
          field: 'numItems',
          label: '# Categories',
          sortable: false,
          class: 'custom-column-110 text-center'
        },
        {
          field: 'numMakes',
          label: '# Makes',
          sortable: false,
          class: 'custom-column-100 text-center'
        },
        {
          field: 'numInfoItems',
          label: '# Info',
          sortable: false,
          class: 'custom-column-80 text-center'
        },
        {
          field: 'isInactive',
          label: 'Inactive?',
          sortable: false,
          class: 'custom-column-60 text-center'
        },
        {
          field: 'adasChecklistMatrix',
          label: '',
          sortable: false,
          class: 'custom-column-60'
        }
      ]
    },
    vehicles() {
      let _data = []
      _data.push({
        id: -9999,
        name: 'all',
        value: -9999
      })
      if (this.lookups['VehicleMake']) {
        this.lookups['VehicleMake'].forEach((element) => {
          _data.push(element)
        })
      }
      return _data
    }
  },
  methods: {
    ...mapActions(adasmatrixStore, [
      'initAdasMatrix',
      'getAdasMatrices',
      'setAdasMatrix',
      'deleteAdasMatrix',
      'resetData',
      'checklistMatrixVehicleKeywordsTriggersExport',
      'getAdasMakeMatrixCategories'
    ]),
    closeModal() {
      this.actionMatrix = null
      this.actionDelete = null
    },
    setOrder(column, sortBy) {
      this.orderBy = column.field
      this.sortBy = sortBy
      this.refreshList()
    },
    async showTab(value) {
      this.resetData('adasmatrices')
      if (value === 'by System') {
        this.refreshList()
      } else if (value === 'by Vehicle') {
        await this.getAdasMakeMatrixCategories()
      }
      this.currentTab = value
    },
    async refreshList() {
      let id = this.filter
      if (id === '-9999' || id === '') {
        id = null
      }
      if (id !== null) {
        id = parseInt(id)
      }
      let options = {
        orderBy: this.orderBy,
        sortBy: this.sortBy,
        status: this.showInactive ? 'Inactive' : '',
        id: id
      }
      await this.getAdasMatrices(options)
    },
    async savedAdasMatrix(data) {
      console.log('Saved Matrix', data)
      await this.refreshList()
      this.closeModal()
      if (data) {
        if (data.isAdd) {
          this.setAdasMatrix(data.id)
          this.actionMatrix = {
            type: 'EDIT',
            vehicleMake: null
          }
        }
      }
    },
    async editAdasMatrix(matrix) {
      console.log('Editing Matrix', matrix)
      let id = this.filter
      if (id === '-9999' || id === '') {
        id = null
      }
      if (id !== null) {
        id = parseInt(id)
      }
      this.setAdasMatrix(matrix.adasChecklistMatrix)
      this.actionMatrix = {
        type: 'EDIT',
        vehicleMake: id
      }
      console.log('Showing Matrix', this.actionMatrix)
    },
    async addAdasMatrix() {
      this.initAdasMatrix()
      this.actionMatrix = {
        type: 'ADD',
        vehicleMake: null
      }
    },
    confirmDelete(matrix) {
      this.actionDelete = matrix
    },
    async performDelete(matrix) {
      this.actionDelete = null
      console.log('Deleting Matrix', matrix.adasChecklistMatrix)
      await this.deleteAdasMatrix(matrix.adasChecklistMatrix)
      await this.refreshList()
    },
    changedMake() {
      this.isReady = false
      if (this.vehicleMake) {
        this.isReady = true
      }
    },
    async fetchMatrices(data) {
      let sortBy = null
      let orderBy = null
      if (data) {
        sortBy = data.sortBy
        orderBy = data.orderBy
      }
      let options = {
        type: 'byVehicle',
        id: this.vehicleMake,
        filter: this.vehicleModel,
        orderBy: orderBy,
        sortBy: sortBy,
        status: this.showInactive ? 'Inactive' : ''
      }
      console.log('Fetching Matrix by Vehicle', options)
      await this.getAdasMatrices(options)
    },
    async exportMatrixVehicleTriggers() {
      console.log('Exporting Matrix Vehicle Triggers')
      this.isDownloading = true
      let columns =
        'AdasChecklistMatrixVehicle,AdasCategory,Make,Model,YearStart,YearEnd,System,Location,LocationKeywords,LaborKeywords,PartsKeywords,Calibration,CalibrationSubTypes,TriggerNotice,ModelExclusions,AddlOperationRequired,AddlOperations'
      await this.checklistMatrixVehicleKeywordsTriggersExport(columns)
      if (this.matrixKeywordsTriggers) {
        var blob = new Blob([this.matrixKeywordsTriggers], { type: 'text/csv' })
        var link = document.createElement('a')
        var now = moment().format('MMDDYYYY')
        if (window.URL.createObjectURL) {
          link.href = window.URL.createObjectURL(blob)
          link.download = `ADAS MAP Vehicle Matrix All Makes Models with Trigger Notice ${now}.csv`
          link.click()
        } else {
          this.message = 'Refresh your browser to generate this download.'
        }
      }
      this.isDownloading = false
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-matrix-wrapper {
  width: 100%;
}
.listingWrapper {
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 450px;
  width: 100%;
}
.custom-spinner {
  width: 100%;
  text-align: center;
  font-size: 1.1em;
}
.custom-show-checkbox {
  position: relative;
  top: 5px;
}
</style>
